// Pinia store outbox offline (P8.G, ADR 0008 §10).
// Persiste compte + liste pending + replay via fetch direct (CSRF inclus).

import { defineStore } from "pinia";
import { outboxDb, type OutboxAction } from "@/shared/offline/outbox-db";

interface OutboxState {
  items: OutboxAction[];
  online: boolean;
  replaying: boolean;
}

function readCsrf(): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

async function replayOne(action: OutboxAction): Promise<boolean> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "Idempotency-Key": action.idempotency_key,
  };
  const csrf = readCsrf();
  if (csrf) headers["X-XSRF-Token"] = csrf;
  try {
    const res = await fetch(action.url, {
      method: action.method,
      headers,
      credentials: "include",
      ...(action.body ? { body: action.body } : {}),
    });
    if (res.ok || res.status === 409 || res.status === 422) {
      // 409/422 = idempotent ou validation : on degage de la file
      await outboxDb.remove(action.id);
      return true;
    }
    if (res.status >= 500) {
      await outboxDb.markFailed(action.id, `HTTP ${String(res.status)}`);
      return false;
    }
    // 4xx autre : le payload est invalide, on retire (sinon boucle infinie)
    await outboxDb.remove(action.id);
    return true;
  } catch (err) {
    await outboxDb.markFailed(action.id, err instanceof Error ? err.message : "network error");
    return false;
  }
}

export const useOutboxStore = defineStore("offline-outbox", {
  state: (): OutboxState => ({
    items: [],
    online: typeof navigator !== "undefined" ? navigator.onLine : true,
    replaying: false,
  }),

  getters: {
    pendingCount: (s) => s.items.length,
    hasPending: (s) => s.items.length > 0,
  },

  actions: {
    async refresh(): Promise<void> {
      this.items = await outboxDb.list();
    },

    async replayAll(): Promise<{ ok: number; failed: number }> {
      if (this.replaying) return { ok: 0, failed: 0 };
      this.replaying = true;
      let ok = 0;
      let failed = 0;
      try {
        const items = await outboxDb.list();
        for (const action of items) {
          const success = await replayOne(action);
          if (success) ok += 1;
          else failed += 1;
        }
        await this.refresh();
      } finally {
        this.replaying = false;
      }
      return { ok, failed };
    },

    async clear(): Promise<void> {
      await outboxDb.clear();
      this.items = [];
    },

    setOnline(online: boolean): void {
      this.online = online;
    },

    bindBrowserEvents(): () => void {
      const onOnline = (): void => {
        this.setOnline(true);
        void this.replayAll();
      };
      const onOffline = (): void => this.setOnline(false);
      const onVisibility = (): void => {
        if (document.visibilityState === "visible" && navigator.onLine) {
          void this.replayAll();
        }
      };
      window.addEventListener("online", onOnline);
      window.addEventListener("offline", onOffline);
      document.addEventListener("visibilitychange", onVisibility);
      return () => {
        window.removeEventListener("online", onOnline);
        window.removeEventListener("offline", onOffline);
        document.removeEventListener("visibilitychange", onVisibility);
      };
    },
  },
});
